import { useRef, useState } from "react";
import { LocationMatcher } from "../../../models/matchers/LocationMatcher";
import { RegexMatcher } from "../../../models/matchers/RegexMatcher";
import { RegexInput } from "../../regex/RegexInput";
import { RegexError } from "../../regex/RegexError";
import { RegexValidatorResponse } from "../../../models/regex/RegexValidator";
import { useLocationHighlight } from "../../hooks/useLocationHighlight";
import { InputGroup } from "../../inputGroup/InputGroup";
import { MatcherLabel } from "../../inputGroup/MatcherLabel";
import { MatcherEditorChildProps } from "../MatcherEditor";
import { Button } from "../../buttons/Button";
import { SectionExtractor } from "../../../services/extraction/SectionExtractor";
import { escapeRegExp } from "../../../services/regex/escapeRegExp";
import {
  PageSection,
  convertAbsoluteToPercentage,
} from "../../../models/matchers/PageSection";

type Props = {
  update: (matcher: LocationMatcher) => void;
} & MatcherEditorChildProps<LocationMatcher>;

const LocationMatcherEditor = (props: Props) => {
  const groupRef = useRef<HTMLSpanElement | null>(null);
  const [error, setError] = useState<RegexValidatorResponse | undefined>();
  const [populating, setPopulating] = useState(false);

  useLocationHighlight(
    props.index ?? 0,
    props.matcher,
    props.matcher.getMatcherId(),
    props.active ?? false, // show highlights on the image viewer when this matcher is active
    (newCoordinates) => {
      props.matcher.section.topLeft.setX(newCoordinates.leftXPercent);
      props.matcher.section.topLeft.setY(newCoordinates.topYPercent);
      props.matcher.section.bottomRight.setX(newCoordinates.rightXPercent);
      props.matcher.section.bottomRight.setY(newCoordinates.bottomYPercent);
      props.update(props.matcher);
    },
  );

  // Coerce nested matcher to RegexMatcher if it is not already one
  if (props.matcher.matcher.getDiscriminator() !== "M_REGEX") {
    props.matcher.matcher = new RegexMatcher({
      discriminator: "M_REGEX",
      position: 0,
      reusable: true,
      threshold: props.defaultMatcherThreshold,
      regex: "",
    });
  }

  const innerRegexMatcher = props.matcher.matcher as RegexMatcher;

  const handlePopulate = async () => {
    if (!props.acceleratorDoc || !props.acceleratorDoc.isHydrated()) return;
    setPopulating(true);
    try {
      const pageIdx = props.pageIndex ?? 0;
      const spansUnderSection = await new SectionExtractor(
        pageIdx,
        props.matcher.section,
      ).extract(props.acceleratorDoc.getHydrated());
      if (spansUnderSection) {
        const page = props.acceleratorDoc.getHydrated().getPage(pageIdx);
        const text = spansUnderSection.spans
          .map((x) => x.unicodeRemovedValue)
          .join(" ");
        innerRegexMatcher.regex = escapeRegExp(text);

        // Also update coordinates from bounding box if any
        if (page) {
          const newPageSection = new PageSection(
            convertAbsoluteToPercentage(spansUnderSection.boundingBox, page),
          );
          if (newPageSection.isConfigured()) {
            props.matcher.section = newPageSection;
          }
        }

        props.update(props.matcher);
      }
    } catch (e) {
      console.error("Could not populate LocationMatcher", e);
    } finally {
      setPopulating(false);
    }
  };

  const showPopulateButton = !innerRegexMatcher.isConfigured();

  return (
    <div
      className={"matcherEditor locationMatcherEditor"}
      onClick={() => props.setActive && props.setActive(true)}
    >
      {/* Inline Location dropdown, regex input box, populate button, and delete button */}
      <InputGroup
        ref={groupRef}
        button={props.button != undefined || showPopulateButton}
      >
        <MatcherLabel label={"Location"} {...props} />

        <RegexInput
          initialValue={innerRegexMatcher.regex || ""}
          parentRef={groupRef}
          onFocus={() => props.setActive && props.setActive(true)}
          updated={(result: RegexValidatorResponse) => {
            setError(result);
            if (result.valid) {
              innerRegexMatcher.regex = result.regex;
              props.update(props.matcher);
            }
          }}
        />
        {showPopulateButton && (
          <Button onClick={handlePopulate} disabled={populating}>
            {populating ? "Populating" : "Populate"}
          </Button>
        )}
        {props.button && props.button}
      </InputGroup>

      <RegexError error={error} />
    </div>
  );
};

export { LocationMatcherEditor };
